import { PayeeStatus } from "#graphql/supergraphTypes";

/**
 * Determines the status of a step in the workflow stepper
 * @param stepIndex - The index of the step to check
 * @param currentStepIndex - The current active step index
 * @returns "active" | "complete" | "inactive"
 */
export const getHeaderStepStatus = (stepIndex: number, currentStepIndex: number) => {
  if (stepIndex === currentStepIndex) {
    return "active";
  } else if (stepIndex < currentStepIndex) {
    return "complete";
  }
  return "inactive";
};

/**
 * Determines if diligence is not confirmed for a payee.
 *
 * Diligence is considered "not confirmed" when:
 * 1. watchlistStatus doesn't exist or is empty, OR
 * 2. any watchlistStatus has a status that is not 'confirmed'
 *
 * @param watchlistStatus - Array of watchlist status objects from the payee's payment preference
 * @returns true if diligence is not confirmed, false otherwise
 */
export const isDiligenceNotConfirmed = (
  watchlistStatus?: Array<{ status: string }> | null,
): boolean => {
  return (
    !watchlistStatus ||
    watchlistStatus.length === 0 ||
    watchlistStatus.some((status) => status.status !== "confirmed")
  );
};

/**
 * Determines if a payee is pending approval in Canopy.
 *
 * Screenings are not triggered until after a payee has been approved,
 * so any non-approved payee (Pending or Rejected) should show "Pending review"
 * rather than "Pending screening review".
 *
 * @param status - The payee's status enum value
 * @returns true if the payee has not yet been approved
 */
export const isPayeePendingApproval = (status?: PayeeStatus | null): boolean => {
  return status !== "approved";
};

export type PayeeReviewTag = "pending-approval" | "pending-screening" | null;

/**
 * Returns which review-related warning tag (if any) should be shown for a payee.
 *
 * Priority mirrors PayeePendingBanner:
 * 1. Payee is not approved in Canopy → "pending-approval" ("Pending review")
 * 2. Payee is approved but watchlist screening is not confirmed → "pending-screening"
 *    ("Pending screening review")
 * 3. Otherwise → null (no tag)
 *
 * @param status - The payee's PayeeStatus
 * @param watchlistStatus - Array of watchlist status objects from the payee's payment preference
 */
export const getPayeeReviewTag = (
  status?: PayeeStatus | null,
  watchlistStatus?: Array<{ status: string }> | null,
): PayeeReviewTag => {
  if (isPayeePendingApproval(status)) {
    return "pending-approval";
  }
  if (isDiligenceNotConfirmed(watchlistStatus)) {
    return "pending-screening";
  }
  return null;
};
